import { useThrottledCallback } from "@react-hookz/web";
import type {
  EmbeddedSpeechifyExperience,
  SpeechifyPlayerConfig,
} from "https://storage.googleapis.com/speechify-api-cdn/speechifyapi.min.mjs";
import { useSetAtom } from "jotai";
import type { RefObject } from "react";
import { useEffect, useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { checkIsPostiesArticle } from "~/components/article/article-render/posties/helpers";
import { SpeechSkipClassName } from "~/components/article/article-speech/consts";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { onlyOnePlayingPlayerAtom } from "~/lib/only-one-playing-player";
import { ZIndexLayer } from "~/lib/styles";
import type { hooksBuildSpeechifyConfigArticle$key } from "~/queries/__generated__/hooksBuildSpeechifyConfigArticle.graphql";

type UseArticleSpeechPlayerParameters = {
  articleSpeechifyPlayer: OrUndefined<EmbeddedSpeechifyExperience>;
};

export const useArticleSpeechPlayer = ({
  articleSpeechifyPlayer,
}: UseArticleSpeechPlayerParameters) => {
  const dispatch = useSetAtom(onlyOnePlayingPlayerAtom);

  const [isInlinePlayerShown, setIsInlinePlayerShown] = useState(false);

  const sendProgressTrackingEvent = useThrottledCallback(
    ({ label }: { label: string }) => {
      sendGATracking({
        action: "TTS/Progress",
        category: "TTS",
        label,
      });
    },
    [sendGATracking],
    1000,
    true,
  );

  useEffect(() => {
    if (!articleSpeechifyPlayer) {
      return;
    }

    const removeEventListener = articleSpeechifyPlayer.addEventListener((event, playerState) => {
      switch (event.type) {
        case "inline-player-shown":
          setIsInlinePlayerShown(true);
          break;
        case "pause":
          dispatch({
            type: "pauseArticleSpeechifyPlayer",
          });
          sendGATracking({
            action: "TTS/Pause",
            category: "TTS",
            label: `${playerState?.progressFraction} - ${playerState?.audioLengthSeconds}`,
          });
          break;
        case "play":
          dispatch({
            type: "playArticleSpeechifyPlayer",
          });
          sendGATracking({
            action: "TTS/Play",
            category: "TTS",
            label: `${playerState?.progressFraction} - ${playerState?.audioLengthSeconds}`,
          });
          break;
        case "progress":
          const percentageElapsed = Math.round((playerState?.progressFraction ?? 0) * 100);
          if (percentageElapsed > 0 && percentageElapsed % 10 === 0) {
            sendProgressTrackingEvent({
              label: `${playerState?.progressFraction} - ${playerState?.audioLengthSeconds}`,
            });
          }
          break;
        case "progress-finished":
          sendGATracking({
            action: "TTS/Progress Finished",
            category: "TTS",
            label: `${playerState?.progressFraction} - ${playerState?.audioLengthSeconds}`,
          });
          break;
        case "resume":
          dispatch({
            type: "playArticleSpeechifyPlayer",
          });
          sendGATracking({
            action: "TTS/Resume",
            category: "TTS",
            label: `${playerState?.progressFraction} - ${playerState?.audioLengthSeconds}`,
          });
          break;
      }
    });
    return () => {
      removeEventListener();
      void articleSpeechifyPlayer.unmount();
    };
  }, [articleSpeechifyPlayer, dispatch, sendProgressTrackingEvent, setIsInlinePlayerShown]);

  return {
    isInlinePlayerShown,
  };
};

type UseBuildSpeechifyConfigParameters = {
  contentParentElement: HTMLElement | null;
  inlinePLayerId?: string;
  inlinePlayerReference: RefObject<HTMLDivElement | null>;
  reference: hooksBuildSpeechifyConfigArticle$key;
};

export const useBuildSpeechifyConfig = ({
  contentParentElement,
  inlinePLayerId,
  inlinePlayerReference,
  reference,
}: UseBuildSpeechifyConfigParameters) => {
  const article = useFragment(
    graphql`
      fragment hooksBuildSpeechifyConfigArticle on Article {
        ...helpersCheckIsPostiesArticle
      }
    `,
    reference,
  );

  const isPostiesArticle = checkIsPostiesArticle(article);

  const builtConfig = useMemo(() => {
    if (!contentParentElement) return null;

    const config = {
      customStyles: {
        playButton: {
          background: "radial-gradient(#ffffff 50%, transparent 50%)",
        },
        primaryColor: "#ff7c1d",
        widget: {
          borderRadius: "8px",
          zIndex: `${ZIndexLayer.Drawer - 1}`,
        },
      },
      ignoreElements(element) {
        return (
          element.tagName === "NOSCRIPT" ||
          element.tagName === "STYLE" ||
          element.tagName === "SCRIPT" ||
          element.classList.contains(SpeechSkipClassName)
        );
      },
      paragraphSettings: {
        isEnabled: "while-playing",
        marginRight: "-10px",
      },
      rootElement: contentParentElement,
      showFeedbackForm: false,
    } as Partial<SpeechifyPlayerConfig>;

    if (!isPostiesArticle) {
      config.inlinePlayerSelector = `#${inlinePLayerId}`;
      config.inlinePlayerElement = inlinePlayerReference.current ?? undefined;
    }

    return config;
  }, [contentParentElement, inlinePLayerId, inlinePlayerReference, isPostiesArticle]);

  return { config: builtConfig };
};
